/**
 * @fileoverview Managed Agents (v2) — frontend-safe UI metadata registry
 * @module jobs/summarization/agents/uiMetadata
 *
 * Purpose:
 * - Expose the manifest fields the browser needs (id, name, group, blurb,
 *   trigger categories, optional dependsOn) without pulling in the rest of
 *   the manifest module — which transitively imports `@cvx/env`, the
 *   Anthropic SDK, and the per-agent Zod schemas. None of that belongs in
 *   the React bundle.
 *
 * Source of truth:
 * - The `id`/`name`/`group`/`uiDescription`/`triggerCategories`/`dependsOn`
 *   fields here MUST match the corresponding manifest file. A unit test
 *   (`uiMetadata.test.ts` — TODO) keeps them in sync.
 *
 * Used by:
 * - src/components/jobs/phases/gather/summarization/SummarizationView.tsx
 */

import type { SummarizationGroupId } from "../types";

export interface ManagedAgentUiMetadata {
  id: string;
  name: string;
  group: SummarizationGroupId;
  uiDescription: string;
  triggerCategories: string[];
  dependsOn?: string[];
}

export const ALL_MANIFEST_UI_METADATA: ReadonlyArray<ManagedAgentUiMetadata> = [
  // -------------------------------------------------------------------------
  // Foundation
  // -------------------------------------------------------------------------
  {
    id: "sum_indicator",
    name: "Complexity Indicator",
    group: "foundation",
    uiDescription:
      "Evaluates job complexity using a traffic light system (green/amber/red) based on four criteria: company has real assets, creditors owed $1M+, director(s) have personal assets, and secured interests flagged.",
    triggerCategories: [
      "corporate_documents",
      "director_disclosures",
      "assets",
      "financial_ledgers",
      "general_liabilities",
      "security_interests",
      "bank_statements_transactions",
      "proof_of_debt",
      "tax_and_sro",
      "loans",
    ],
  },
  {
    id: "sum_corporate",
    name: "Corporate",
    group: "foundation",
    uiDescription:
      "Establishes entity identity, corporate structure, cap table, officeholder history, and corporate event timeline.",
    triggerCategories: ["corporate_documents", "director_disclosures"],
  },
  {
    id: "sum_board_reports",
    name: "Board Minutes & Reports",
    group: "foundation",
    uiDescription:
      "Identifies signs of financial distress in board meeting minutes and reports, and material transactions discussed, for future analysis of director conduct.",
    triggerCategories: ["corporate_documents"],
  },
  {
    id: "sum_related_parties",
    name: "Related Parties",
    group: "foundation",
    uiDescription:
      "Builds an evidence chain for related/connected parties from corporate registers, director disclosures, and financial records. Cross-references officeholders, shareholders, and disclosed associates.",
    triggerCategories: ["corporate_documents", "director_disclosures"],
    dependsOn: ["sum_corporate"],
  },

  // -------------------------------------------------------------------------
  // Financial
  // -------------------------------------------------------------------------
  {
    id: "sum_bank_transactions",
    name: "Bank Transactions",
    group: "financial",
    uiDescription:
      "Categorises bank transactions by counterparty type (related party, employee, trade, government, etc.) and groups by party for investigation analysis.",
    triggerCategories: ["bank_statements_transactions"],
  },
  {
    id: "sum_financial_analysis",
    name: "Financial Analysis",
    group: "financial",
    uiDescription:
      "Extracts period-indexed financial data from P&L, Balance Sheet, Trial Balance, and GL records. Produces financial analysis worksheets and algorithmically computed insolvency indicators.",
    triggerCategories: ["financial_ledgers", "general_ledgers"],
  },

  // -------------------------------------------------------------------------
  // Assets
  // -------------------------------------------------------------------------
  {
    id: "sum_asset_list",
    name: "Assets",
    group: "assets",
    uiDescription:
      "Generates a comprehensive asset summary with valuations from ROCAPs, asset registers, inventory records, PPSR data, balance sheets, and bank statement closing balances. Cross-references director disclosures, assets, securities, financial ledgers, and banking records.",
    triggerCategories: [
      "director_disclosures",
      "assets",
      "security_interests",
      "financial_ledgers",
      "bank_statements_transactions",
    ],
  },
  {
    id: "sum_asset_sales",
    name: "Asset Sales & Purchases",
    group: "assets",
    uiDescription:
      "Generates a structured register of sales and purchases identified in financial ledgers, general ledgers, and asset sale/purchase agreements to support later asset disposal analysis.",
    triggerCategories: ["assets", "financial_ledgers", "general_ledgers"],
  },

  // -------------------------------------------------------------------------
  // Liabilities
  // -------------------------------------------------------------------------
  {
    id: "sum_security_interests",
    name: "Security Interests",
    group: "liabilities",
    uiDescription:
      "Identifies all PPSR registrations for asset analysis and potentially voidable security interests. Cross-references PPSR data, director disclosures, securities documents, asset records, and loan agreements.",
    triggerCategories: ["security_interests", "proof_of_debt"],
  },
  {
    id: "sum_employment",
    name: "Preferred Creditors",
    group: "liabilities",
    uiDescription:
      "Identifies employee priority creditors and their individual entitlements from payroll records, STP, employment agreements, ROCAPs, aged payables, and lodged Proofs of Debt.",
    triggerCategories: [
      "employment",
      "director_disclosures",
      "general_liabilities",
      "proof_of_debt",
    ],
  },
  {
    id: "sum_general_liabilities",
    name: "Creditors",
    group: "liabilities",
    uiDescription:
      "Identifies general and trade liabilities and matches them with invoices, agreements, and lodged Proofs of Debt. Forms part of analysis of liabilities and proof of debt processing.",
    triggerCategories: [
      "general_liabilities",
      "director_disclosures",
      "bank_statements_transactions",
      "proof_of_debt",
    ],
  },
  {
    id: "sum_loans",
    name: "Loans",
    group: "liabilities",
    uiDescription:
      "Identifies loans entered into by the Company (granted and taken) for future analysis of voidable transactions. Cross-references loan agreements, director disclosures, financial ledgers, general ledgers, and PPSR data.",
    triggerCategories: [
      "loans",
      "director_disclosures",
      "financial_ledgers",
      "general_ledgers",
      "security_interests",
    ],
  },
  {
    id: "sum_ato_sro",
    name: "ATO & SRO",
    group: "liabilities",
    uiDescription:
      "Cross-references ATO/SRO documents, director disclosures, bank transactions, and lodged Proofs of Debt to produce a summary of tax authority liabilities with flags for balance mismatches, round payments, payment plans, warning letters, and DPNs.",
    triggerCategories: [
      "tax_and_sro",
      "director_disclosures",
      "bank_statements_transactions",
      "proof_of_debt",
    ],
    dependsOn: ["sum_bank_transactions"],
  },
];
